import React from 'react';
import { Box, CircularProgress, Stack, Typography, styled } from '@mui/material';
import DoneAllRoundedIcon from '@mui/icons-material/DoneAllRounded';
import { useTranslationContext } from 'contexts/TranslationContext/TranslationContext';
import {
  DayStatusIndicator,
  DayStatusIndicatorState,
  getCompletionPercentage,
} from 'components/DayStatusIndicator/DayStatusIndicator';
import { WidgetCard } from 'components/WidgetCard/WidgetCard';
import { CompletionStat, useGetStatsQuery } from 'services/statsService';

const TODAY_RING_SIZE = 140;
const TODAY_RING_THICKNESS = 3;
const DAYS_IN_WEEK = 7;

const ContentStack = styled(Stack)(({ theme }) => ({
  alignItems: 'center',
  gap: theme.spacing(4),
}));

const RingWrapper = styled(Box)(() => ({
  position: 'relative',
  width: TODAY_RING_SIZE,
  height: TODAY_RING_SIZE,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

const RingLabel = styled(Box)(() => ({
  position: 'absolute',
  inset: 0,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
}));

const DaysRow = styled(Stack)(({ theme }) => ({
  width: '100%',
  flexDirection: 'row',
  // space-evenly keeps equal gaps and edge margins so the end indicators never clip.
  justifyContent: 'space-evenly',
  [theme.breakpoints.down('sm')]: {
    gap: theme.spacing(1),
  },
}));

const LoadingState = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  padding: theme.spacing(6),
}));

const ErrorState = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  padding: theme.spacing(3),
}));

// Solid green shown when the ring reaches 100%; no matching token in the theme palette.
const RING_COMPLETE_COLOR = '#73BE5E';

const RingTrack = styled(CircularProgress)(({ theme }) => ({
  color: theme.palette.brandBackgroundSecondary.main,
}));

const RingValue = styled(CircularProgress, {
  shouldForwardProp: (prop) => prop !== 'isComplete',
})<{ isComplete: boolean }>(({ theme, isComplete }) => ({
  color: isComplete ? RING_COMPLETE_COLOR : theme.palette.primary.main,
  position: 'absolute',
  left: 0,
  '& .MuiCircularProgress-circle': { strokeLinecap: 'round' },
}));

const RingTitle = styled(Typography)(({ theme }) => ({
  fontWeight: theme.typography.fontWeightMedium,
  lineHeight: 1.2,
  color: theme.palette.grey[700],
}));

const RingPercentage = styled(Typography)(({ theme }) => ({
  lineHeight: 1.2,
  color: theme.palette.grey[900],
}));

// Single-letter weekday; parse as local time so the date doesn't shift a day in UTC-negative zones.
const getDayLabel = (date: string) =>
  new Date(`${date}T00:00:00`).toLocaleDateString('en-US', { weekday: 'narrow' });

const toDateKey = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Normalize to 7 consecutive days ending on the latest date, filling gaps with empty (noActivity) stats.
const buildWeekStats = (taskStats: CompletionStat[]): CompletionStat[] => {
  if (taskStats.length === 0) return [];

  const statsByDate = new Map(taskStats.map((stat) => [stat.date, stat]));
  const latestKey = taskStats.reduce(
    (latest, stat) => (stat.date > latest ? stat.date : latest),
    taskStats[0].date,
  );
  const endDate = new Date(`${latestKey}T00:00:00`);

  return Array.from({ length: DAYS_IN_WEEK }, (_, index) => {
    const date = new Date(endDate);
    date.setDate(endDate.getDate() - (DAYS_IN_WEEK - 1 - index));
    const key = toDateKey(date);
    return statsByDate.get(key) ?? { completed: 0, total: 0, date: key };
  });
};

const getDayState = (stat: CompletionStat, isCurrentDay: boolean): DayStatusIndicatorState => {
  if (stat.total <= 0) return 'noActivity';
  if (stat.completed >= stat.total) return 'completedAll';
  if (stat.completed <= 0) return isCurrentDay ? 'currentDayEmpty' : 'noneCompleted';
  return 'someCompleted';
};

const TodayRing = ({ percentage, label }: { percentage: number; label: string }) => {
  const isComplete = percentage >= 100;
  return (
    <RingWrapper>
      <RingTrack
        variant="determinate"
        value={100}
        size={TODAY_RING_SIZE}
        thickness={TODAY_RING_THICKNESS}
        aria-hidden
      />
      {percentage > 0 && (
        <RingValue
          variant="determinate"
          value={percentage}
          size={TODAY_RING_SIZE}
          thickness={TODAY_RING_THICKNESS}
          aria-label={`${label} ${percentage}%`}
          isComplete={isComplete}
        />
      )}
      <RingLabel>
        <RingTitle variant="footnote">{label}</RingTitle>
        <RingPercentage variant="title3">{percentage}%</RingPercentage>
      </RingLabel>
    </RingWrapper>
  );
};

export interface ChecklistCompletionWidgetViewProps {
  taskStats: CompletionStat[];
  isLoading?: boolean;
  isError?: boolean;
}

export const ChecklistCompletionWidgetView = ({
  taskStats,
  isLoading = false,
  isError = false,
}: ChecklistCompletionWidgetViewProps) => {
  const { translation } = useTranslationContext();

  const weekStats = buildWeekStats(taskStats);
  const lastIndex = weekStats.length - 1;
  const todayStat = weekStats[lastIndex];
  const todayPercentage = todayStat
    ? Math.round(getCompletionPercentage(todayStat.completed, todayStat.total))
    : 0;

  return (
    <WidgetCard icon={<DoneAllRoundedIcon />} title={translation('checklist_completion')}>
      {isLoading && (
        <LoadingState>
          <CircularProgress aria-label={translation('checklist_completion')} />
        </LoadingState>
      )}
      {!isLoading && isError && (
        <ErrorState>
          <Typography variant="footnote" role="alert">
            {translation('data_unable_to_load')}
          </Typography>
        </ErrorState>
      )}
      {!isLoading && !isError && (
        <ContentStack>
          <TodayRing percentage={todayPercentage} label={translation('today_all_caps')} />
          <DaysRow>
            {weekStats.map((stat, index) => {
              const isCurrentDay = index === lastIndex;
              return (
                <DayStatusIndicator
                  key={stat.date}
                  label={getDayLabel(stat.date)}
                  state={getDayState(stat, isCurrentDay)}
                  underlineLabel={isCurrentDay}
                  completionData={{ completed: stat.completed, total: stat.total }}
                />
              );
            })}
          </DaysRow>
        </ContentStack>
      )}
    </WidgetCard>
  );
};

export const ChecklistCompletionWidget = () => {
  const { data, isLoading, isError } = useGetStatsQuery();

  return (
    <ChecklistCompletionWidgetView
      taskStats={data?.task_stats ?? []}
      isLoading={isLoading}
      isError={isError && data == null}
    />
  );
};
